import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { AuthApiError } from './api';

/** Shows the Face ID / Windows Hello prompt to create a passkey. */
export const createPasskey = (optionsJSON: PublicKeyCredentialCreationOptionsJSON) =>
  startRegistration({ optionsJSON });

/** Shows the Face ID / Windows Hello prompt to use a passkey. */
export const askForPasskey = (optionsJSON: PublicKeyCredentialRequestOptionsJSON) =>
  startAuthentication({ optionsJSON });

/** Turns a lockout time into "3:45 p.m." for display. */
export function formatLockedUntil(iso: string | undefined): string {
  if (!iso) return 'a while';
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** A plain-language explanation for anything that can go wrong in a sign-in step. */
export function describeAuthError(err: unknown): string {
  if (err instanceof AuthApiError) {
    switch (err.code) {
      case 'locked':
        return `Too many wrong tries. Try again after ${formatLockedUntil(err.lockedUntil)}.`;
      case 'invalid_token':
        return "That setup token isn't right.";
      case 'invalid_code':
        return "That code didn't work. Check it and try again.";
      case 'sign_in_failed':
        return "That passkey wasn't accepted.";
      case 'not_found':
        return 'Setup has already been completed.';
      case 'already_set_up':
        return 'Someone finished setup a moment ago.';
      case 'challenge_invalid':
        return 'That took too long. Please try again.';
      case 'registration_failed':
        return "Couldn't save that passkey. It may already be registered.";
      case 'last_passkey':
        return "You can't remove your last passkey.";
      case 'already_enabled':
        return 'The authenticator app is already on.';
      case 'no_owner':
        return 'Nobody has set this up yet.';
      case 'too_many_requests':
        return 'Too many requests. Wait a minute and try again.';
      case 'csrf':
        return "The request was blocked. Reload the page and try again. Make sure you're on the right address.";
      case 'unauthorized':
        return 'Your session ended. Sign in again.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }
  if (err instanceof Error) {
    switch (err.name) {
      case 'NotAllowedError':
        return 'Cancelled, or it took too long. Try again.';
      case 'InvalidStateError':
        return 'That passkey is already registered on this device.';
      case 'SecurityError':
        return "This address doesn't match the one passkeys are tied to. Open the app from its real address.";
      case 'AbortError':
        return 'Cancelled.';
      case 'NotSupportedError':
        return "This device or browser can't make passkeys.";
    }
  }
  return "Couldn't reach the server. Check your connection and try again.";
}
